import React from 'react';
import firebase from 'firebase/app';
import { useCurrentAgreement, useCurrentUser } from '@economic/react-app';
import { FirestoreUserPreferences } from '../../../types';

export default function useFirestore(): [
    firebase.firestore.DocumentReference | undefined,
    firebase.firestore.DocumentReference | undefined,
    firebase.firestore.Firestore | undefined
] {
    const agreement = useCurrentAgreement();
    const user = useCurrentUser();

    if (
        !process.env.REACT_APP_FIRESTORE_API_KEY ||
        !process.env.REACT_APP_FIRESTORE_API_KEY ||
        !process.env.REACT_APP_FIRESTORE_PROJECT_ID
    ) {
        return [undefined, undefined, undefined];
    }

    const db = firebase.firestore();
    const agreementDoc = db.collection('agreements').doc(String(agreement.number));
    const userDoc = agreementDoc.collection('users').doc(user.guid);

    return [agreementDoc, userDoc, db];
}

export const useFirestoreUserPreferences = (): [
    FirestoreUserPreferences | undefined,
    (changeset: Partial<FirestoreUserPreferences>) => void,
    boolean
] => {
    const [, user] = useFirestore();
    const [loading, setLoading] = React.useState(true);
    const [preferences, setPreferences] = React.useState<FirestoreUserPreferences | undefined>(undefined);

    const fetch = async () => {
        try {
            if (user) {
                const prefs: any = await (await user?.get()).data();
                setPreferences(
                    {
                        ...prefs,
                        lastSeenNotificationsTimestamp: prefs.lastSeenNotificationsTimestamp?.toDate(),
                    } || {}
                );
            }
        } catch {
            // oh well
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetch();
    }, []);

    const handleSetPreferences = async (changeset: Partial<FirestoreUserPreferences>) => {
        try {
            await user?.set(changeset, { merge: true });
        } catch {
            // oh well
        } finally {
            setPreferences(prefs => ({ ...prefs, ...changeset }));
        }
    };

    return [preferences, handleSetPreferences, loading];
};
